import { createSlice } from '@reduxjs/toolkit';
import {
  adminDeleteContactUs,
  adminMarkAllContactUsRead,
  adminMarkAllContactUsUnread,
  adminToggleReadContactUs,
  toggleReadContactId,
  toggleReadTarget,
  getAdminContactUsList,
  getAdminContactUsUnreadCount,
} from '../actions/adminContactUsActions';
import { getPageFromUrl } from '../../../utils/paginationUtil';

const initialState: AdminContactUsStateType = {
  submissions: [],
  isLoading: false,
  errorMessage: '',
  unreadCount: 0,
  paginator: { count: 0, next: null, previous: null, currentPage: 1, pageSize: 12 },
  latestFetchRequestId: null,
  latestToggleRequestIdByContactId: {},
};

const adminContactUsSlice = createSlice({
  name: 'adminContactUs',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Rapid page switching can fire several getAdminContactUsList calls in a
    // row; whichever settles first is no longer the one the user cares about.
    // Only the most recently dispatched request's requestId is tracked, and
    // fulfilled/rejected below ignore any response whose requestId doesn't
    // match it, so a stale response can never clobber newer state.
    builder.addCase(getAdminContactUsList.pending, (state, action) => {
      state.isLoading = true;
      state.errorMessage = '';
      state.latestFetchRequestId = action.meta.requestId;
    });
    builder.addCase(getAdminContactUsList.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.latestFetchRequestId) {
        return;
      }
      state.isLoading = false;
      state.submissions = action.payload.results;
      state.unreadCount = action.payload.unread_count;
      state.paginator = {
        count: action.payload.count,
        next: getPageFromUrl(action.payload.next),
        previous: getPageFromUrl(action.payload.previous),
        currentPage: action.meta.arg.page ?? 1,
        pageSize: action.payload.page_size,
      };
    });
    builder.addCase(getAdminContactUsList.rejected, (state, action) => {
      if (action.meta.requestId !== state.latestFetchRequestId) {
        return;
      }
      state.isLoading = false;
      state.errorMessage = action.payload?.detail || 'Failed to load submissions.';
    });

    builder.addCase(getAdminContactUsUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload.unread_count;
    });

    // Optimistic: flip the row's read state as soon as the request goes out
    // (based on its current local state) rather than waiting for the
    // round-trip, and nudge unreadCount by +/-1 to match. A rapid
    // double-toggle of the same row (e.g. clicking the mail icon then the
    // row, or double-clicking) fires two requests whose responses can settle
    // out of order; `latestToggleRequestIdByContactId` tracks the newest
    // request per row so fulfilled/rejected below ignore a stale one instead
    // of re-applying a delta on top of a newer optimistic update.
    builder.addCase(adminToggleReadContactUs.pending, (state, action) => {
      const contactId = toggleReadContactId(action.meta.arg);
      const submission = state.submissions.find(s => s.id === contactId);
      const target = toggleReadTarget(action.meta.arg);
      // An explicit set that matches what's shown changes nothing locally, so
      // don't track it: its rejected case would "revert" a change never made.
      if (submission && target !== undefined && target === submission.is_read) {
        return;
      }
      // Persisted (localStorage) state from before this map existed won't have
      // it, so it's created lazily here rather than assumed to exist.
      if (!state.latestToggleRequestIdByContactId) {
        state.latestToggleRequestIdByContactId = {};
      }
      state.latestToggleRequestIdByContactId[contactId] = action.meta.requestId;
      if (submission) {
        const newIsRead = target ?? !submission.is_read;
        state.unreadCount = Math.max(0, state.unreadCount + (newIsRead ? -1 : 1));
        submission.is_read = newIsRead;
      }
    });
    builder.addCase(adminToggleReadContactUs.fulfilled, (state, action) => {
      const updated = action.payload.submission;
      if (action.meta.requestId !== state.latestToggleRequestIdByContactId?.[updated.id]) {
        return;
      }
      const idx = state.submissions.findIndex(s => s.id === updated.id);
      if (idx !== -1) {
        // A bulk action (mark all as read/unread) can have changed this row's
        // is_read - and unreadCount - while this toggle was in flight, making
        // the optimistic delta applied at `pending` wrong. Reconcile against
        // the server's authoritative value instead of trusting that delta.
        if (state.submissions[idx].is_read !== updated.is_read) {
          state.unreadCount = Math.max(0, state.unreadCount + (updated.is_read ? -1 : 1));
        }
        state.submissions[idx] = updated;
      }
    });
    builder.addCase(adminToggleReadContactUs.rejected, (state, action) => {
      const contactId = toggleReadContactId(action.meta.arg);
      if (action.meta.requestId !== state.latestToggleRequestIdByContactId?.[contactId]) {
        return;
      }
      state.errorMessage = action.payload?.detail || 'Failed to update submission.';
      const submission = state.submissions.find(s => s.id === contactId);
      if (submission) {
        const reverted = !submission.is_read;
        state.unreadCount = Math.max(0, state.unreadCount + (reverted ? -1 : 1));
        submission.is_read = reverted;
      }
    });

    builder.addCase(adminMarkAllContactUsRead.fulfilled, state => {
      state.submissions.forEach(s => {
        s.is_read = true;
      });
      state.unreadCount = 0;
    });
    builder.addCase(adminMarkAllContactUsRead.rejected, (state, action) => {
      state.errorMessage = action.payload?.detail || 'Failed to mark all as read.';
    });

    // Marks every submission across all pages as unread server-side, so the
    // local count is set from the paginator's total (not submissions.length,
    // which only covers the current page).
    builder.addCase(adminMarkAllContactUsUnread.fulfilled, state => {
      state.submissions.forEach(s => {
        s.is_read = false;
      });
      state.unreadCount = state.paginator.count;
    });
    builder.addCase(adminMarkAllContactUsUnread.rejected, (state, action) => {
      state.errorMessage = action.payload?.detail || 'Failed to mark all as unread.';
    });

    builder.addCase(adminDeleteContactUs.fulfilled, (state, action) => {
      const { contactId } = action.payload;
      const submission = state.submissions.find(s => s.id === contactId);
      if (submission && !submission.is_read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.submissions = state.submissions.filter(s => s.id !== contactId);
      state.paginator.count = Math.max(0, state.paginator.count - 1);
    });
    builder.addCase(adminDeleteContactUs.rejected, (state, action) => {
      state.errorMessage = action.payload?.detail || 'Failed to delete submission.';
    });
  },
});

export default adminContactUsSlice.reducer;
